import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../software/api";
import { refNo } from "../../../software/format";
import { toast } from "sonner";
import { X, Search, Bell, Trash2, Pencil, BellOff, ShoppingCart, Plus } from "lucide-react";
import { gold, money, todayISO, fmtDate, statusLabel, noteSubject, FALLBACK_BRANDS, HdrBtn, cellInp, isLockedStatus } from "./shared";
import {
  ClientDetails, WorkOrdersTab, QuotesTab, InvoicesTab, ContractsTab, EquipmentTab, NotesTab,
  LegacyWorkOrderView, printQuoteDoc, printInvoiceDoc,
} from "./tabs";
import { WaterTestTab } from "./WaterTestTab";
import WorkOrderSetupModal from "./WorkOrderSetupModal";
import NextServiceModal from "./NextServiceModal";
import AddWorkOrderPart from "./AddWorkOrderPart";
import AddLabour from "./AddLabour";
import { printNotes, registerCompanyInfo } from "./printDocs";
import { printLegacyStatement, printLegacyDelivery, ensureCompanyInfo, ensureScripts } from "../reports/legacy/docs";
import OrdersForm from "./OrdersForm";
import {
  DocEditor, FilesModal, CompanyDocsModal, FM84Modal, PartsOrdersModal, SuppliersModal,
  InvoiceDetailsModal, PaymentArrangementModal, DeliveryByModal, StockAllocatorModal,
} from "./modals";
import ClientFolders from "./ClientFolders";
import { InvoicePaymentHow } from "./InvoicePaymentHow";
import { EmailRecipientsModal } from "./EmailRecipientsModal";
import { ClientReminderCard } from "./ClientReminderCard";
import SmsClientModal from "./SmsClientModal";

function LoyaltyField({ client }) {
  const [val, setVal] = useState(client.loyalty_number || "");
  const [saving, setSaving] = useState(false);
  const saved = useRef(client.loyalty_number || "");
  useEffect(() => { setVal(client.loyalty_number || ""); saved.current = client.loyalty_number || ""; }, [client.id, client.loyalty_number]);
  const save = async () => {
    const value = (val || "").trim();
    if (value === (saved.current || "")) return;
    setSaving(true);
    try {
      await api("PATCH", `/clients/${client.id}/loyalty`, { loyalty_number: value });
      saved.current = value; client.loyalty_number = value;
      toast.success(value ? "Loyalty number saved — 10% member discount now applies" : "Loyalty number cleared");
    } catch (e) { toast.error(e.message || "Could not save loyalty number"); setVal(saved.current || ""); }
    finally { setSaving(false); }
  };
  return (
    <div className="flex items-center gap-1.5" data-testid="co-loyalty-field">
      <span className="text-[10px] font-bold text-slate-800 leading-[1.05] text-right whitespace-nowrap uppercase tracking-tight">Loyalty<br />Number</span>
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); if (e.key === "Escape") { setVal(saved.current || ""); e.target.blur(); } }}
        placeholder="Scan card #"
        disabled={saving}
        data-testid="co-loyalty-input"
        className="bg-white border border-[#b9b9b9] px-2 py-0.5 text-[12px] font-mono2 w-28 outline-none focus:border-[#123a63] disabled:opacity-60"
      />
    </div>
  );
}





export default function ClientOverview({ client, user, isAdmin, initialTab, initialWoId, onClose, onSwitchboard, onNewClient, onGoTo, onEditClient, onDoWaterTest, onReport, onSellAtPos, onClientSaved, onNav, remBadge = 0, autoAction, onAutoActionDone }) {
  const [tab, setTab] = useState(initialTab || "work_orders");
  const [wos, setWos] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [sales, setSales] = useState([]);
  const [legacy, setLegacy] = useState({ invoices: [], quotes: [], workorders: [], pos: [], payments: [], counts: {} });
  const [reminders, setReminders] = useState([]);
  const [notes, setNotes] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [bodies, setBodies] = useState([]);
  const [tests, setTests] = useState([]);
  const [files, setFiles] = useState([]);
  const [fm84s, setFm84s] = useState([]);
  const [partsOrders, setPartsOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companyDocs, setCompanyDocs] = useState([]);
  const [team, setTeam] = useState([]);
  const [brands, setBrands] = useState(FALLBACK_BRANDS);

  const [selWoId, setSelWoId] = useState(initialWoId || null);
  const [woEdit, setWoEdit] = useState(null);
  const [selQuoteId, setSelQuoteId] = useState(null);
  const [selSaleId, setSelSaleId] = useState(null);
  const [purch, setPurch] = useState(null);
  useEffect(() => { setPurch(null); api("GET", `/clients/${client.id}/purchase-history`).then(setPurch).catch(() => setPurch({ purchases: [], total_spent: 0 })); }, [client.id]); // eslint-disable-line
  const [pickPart, setPickPart] = useState(false);
  const [pickLabour, setPickLabour] = useState(false);
  const [rem, setRem] = useState({ due_date: todayISO(), assignee: "", text: "" });
  const [noteText, setNoteText] = useState("");
  const [noteSubj, setNoteSubj] = useState("");
  const [viewNote, setViewNote] = useState(null);
  const [showRemModal, setShowRemModal] = useState(false);
  const [showNoteModal, setShowNoteModal] = useState(false);
  const [showFiles, setShowFiles] = useState(false);
  const [showSms, setShowSms] = useState(false);
  const [showRemList, setShowRemList] = useState(false);
  const [showCompanyDocs, setShowCompanyDocs] = useState(false);
  const [showSuppliers, setShowSuppliers] = useState(false);
  const [showPartsOrders, setShowPartsOrders] = useState(false);
  const [showAllOrders, setShowAllOrders] = useState(false);
  const [partsOrderWo, setPartsOrderWo] = useState(undefined); // undefined=closed, {wo}|{} = open
  const [fm84Modal, setFm84Modal] = useState(null);
  const [quoteEditor, setQuoteEditor] = useState(null);
  const [woSetup, setWoSetup] = useState(null);
  const [nextFor, setNextFor] = useState(null);
  const [invoiceBuilder, setInvoiceBuilder] = useState(false);
  const [payModal, setPayModal] = useState(null);
  const [invDetails, setInvDetails] = useState(null);
  const [payArrange, setPayArrange] = useState(null);
  const [deliveryBy, setDeliveryBy] = useState(null);
  const [stockAlloc, setStockAlloc] = useState(null);
  const [invoiceEditor, setInvoiceEditor] = useState(null);
  const [emailModal, setEmailModal] = useState(null);
  const [mailNew, setMailNew] = useState(0);
  useEffect(() => {
    const f = () => api("GET", `/mail/alerts?client_id=${client.id}`).then((a) => setMailNew(a.new)).catch(() => {});
    f(); window.addEventListener("aqualux:mail-changed", f); const t = setInterval(f, 60000);
    return () => { clearInterval(t); window.removeEventListener("aqualux:mail-changed", f); };
  }, [client.id]); // {subject, kind, doc}
  const openEmail = (opts) => setEmailModal(opts);
  const cid = client.id;
  useEffect(() => { ensureCompanyInfo().then(ensureScripts); }, []);

  const loadAll = useCallback(async () => {
    const [w, q, s, rlist, nl, ct, eq, bd, ts, fl, fm, po, lh] = await Promise.all([
      api("GET", `/workorders?client_id=${cid}`),
      api("GET", `/quotes?client_id=${cid}`),
      api("GET", `/sales?client_id=${cid}`),
      api("GET", `/reminders?include_done=true&client_id=${cid}`),
      api("GET", `/clients/${cid}/notes`),
      api("GET", `/clients/${cid}/contracts`),
      api("GET", `/clients/${cid}/equipment`),
      api("GET", `/clients/${cid}/waterbodies`),
      api("GET", `/clients/${cid}/tests`),
      api("GET", `/clients/${cid}/files`),
      api("GET", `/fm84?client_id=${cid}`),
      api("GET", `/parts-orders?client_id=${cid}`),
      api("GET", `/clients/${cid}/legacy-history`).catch(() => ({ invoices: [], quotes: [], workorders: [], pos: [], payments: [], counts: {} })),
    ]);
    setWos(w); setQuotes(q); setSales(s); setLegacy(lh || { invoices: [], quotes: [], workorders: [], pos: [], payments: [], counts: {} });
    setReminders(rlist.filter((r) => r.client_id === cid));
    setNotes(nl); setContracts(ct); setEquipment(eq); setBodies(bd); setTests(ts); setFiles(fl); setFm84s(fm); setPartsOrders(po);
    if (w.length) setSelWoId((prev) => prev || w[0].id);
    { const firstSpa = q.find((x) => x.kind === "spa" && !x.invoice_id && !x.converted_wo_id && !["converted", "lost", "declined", "invoiced", "cancelled", "no sale"].includes(String(x.status || "").toLowerCase())); if (firstSpa) setSelQuoteId((prev) => prev || firstSpa.id); }
    if (s.length) setSelSaleId((prev) => prev || s[0].id);
    return w;
  }, [cid]); // eslint-disable-line

  useEffect(() => { loadAll(); }, [loadAll]);
  const loadSuppliers = useCallback(() => api("GET", "/suppliers").then(setSuppliers).catch(() => {}), []);
  const loadCompanyDocs = useCallback(() => api("GET", "/company-docs").then(setCompanyDocs).catch(() => {}), []);
  useEffect(() => {
    api("GET", "/team").then((t) => setTeam(t.map((x) => x.name).filter(Boolean))).catch(() => {});
    api("GET", "/brand-profiles").then((bp) => {
      const names = (bp || []).map((p) => p.company || p.name).filter(Boolean);
      setBrands(Array.from(new Set([...FALLBACK_BRANDS, ...names])));
    }).catch(() => {});
    api("GET", "/company-setup").then((cs) => {
      const map = {};
      (cs || []).forEach((c) => {
        if (!c.name) return;
        // relative logo paths (e.g. /justspas-newcastle-logo.png) → absolute so they render in print popups on any deployed domain
        const lu = c.logo_url && c.logo_url.startsWith("/") ? `${window.location.origin}${c.logo_url}` : c.logo_url;
        map[c.name.trim().toLowerCase()] = { ...c, logo_url: lu };
      });
      registerCompanyInfo(map);
    }).catch(() => {});
    loadSuppliers(); loadCompanyDocs();
  }, [loadSuppliers, loadCompanyDocs]);

  const suburb = (client.address?.suburb || "").toUpperCase();
  const surname = client.surname || client.contacts?.[0]?.surname || client.name?.split(" ").slice(-1)[0] || client.name;
  const title = client.display_name || (suburb ? `${surname} - ${suburb}` : client.name);
  const street = [client.address?.street, client.address?.suburb].filter(Boolean).join(", ");
  const contactRows = client.contacts?.length ? client.contacts
    : [{ first_name: (client.name || "").split(" ")[0], surname, mobile: client.phone }];
  const brand = brands[0] || "Aqualux Nexus";

  const selWo = useMemo(() => wos.find((w) => w.id === selWoId) || null, [wos, selWoId]);
  const hydrateWo = (w) => {
    let lines = w.lines || [];
    if (!lines.length) {
      lines = [
        ...(w.parts || []).map((p) => ({ kind: "part", item: p.name, product_id: p.product_id, unlocked: false, unit: p.unit_price, qty: p.qty, gross: p.qty * p.unit_price, discount: 0, net: p.qty * p.unit_price })),
        ...(w.labour || []).map((l) => ({ kind: "labour", item: l.description || "Labour", unlocked: true, unit: l.rate, qty: l.hours, gross: l.hours * l.rate, discount: 0, net: l.hours * l.rate })),
      ];
    }
    return { ...w, lines };
  };
  useEffect(() => { setWoEdit(selWo ? hydrateWo(selWo) : null); woBase.current = selWo ? hydrateWo(selWo) : null; }, [selWoId, !!selWo]); // eslint-disable-line
  // live link with Pool-Tech: re-read this client's work orders every 5s; office edits win only on the fields the office changed
  const woBase = useRef(null), woEditRef = useRef(null);
  woEditRef.current = woEdit;
  useEffect(() => {
    const t = setInterval(async () => {
      if (document.visibilityState !== "visible") return;
      const w = await api("GET", `/workorders?client_id=${cid}`).catch(() => null);
      if (!w) return;
      setWos((old) => (JSON.stringify(old) === JSON.stringify(w) ? old : w));
      // keep the state updater pure (StrictMode runs it twice): work out base/fresh first, then merge
      const base = woBase.current, f = base && w.find((x) => x.id === base.id);
      if (!f) return;
      const fresh = hydrateWo(f);
      if (JSON.stringify(fresh) === JSON.stringify(base)) return;
      woBase.current = fresh;
      const cur = woEditRef.current;
      if (cur && cur.id === base.id && JSON.stringify(cur.lines) !== JSON.stringify(base.lines) && JSON.stringify(fresh.lines) !== JSON.stringify(base.lines)) toast.warning(`The technician changed the lines on W/O ${f.wo_number} while you were editing them — check before saving`);
      setWoEdit((prev) => {
        if (!prev || prev.id !== base.id) return prev;
        const out = { ...fresh };
        Object.keys(prev).forEach((k) => { if (JSON.stringify(prev[k]) !== JSON.stringify(base[k])) out[k] = prev[k]; });
        return out;
      });
    }, 5000);
    return () => clearInterval(t);
  }, [cid]); // eslint-disable-line
  // reload all data AND re-hydrate the open work order from the server (used after server-side WO changes)
  const reloadSyncWo = useCallback(async () => {
    const w = await loadAll();
    setWoEdit((prev) => {
      if (!prev) return prev;
      const fresh = (w || []).find((x) => x.id === prev.id);
      return fresh ? hydrateWo(fresh) : prev;
    });
  }, [loadAll]);

  const recalc = (lines) => lines.map((l) => {
    const gross = Math.round((l.qty || 0) * (l.unit || 0) * 100) / 100;
    return { ...l, gross, net: Math.round((gross - (l.discount || 0)) * 100) / 100 };
  });
  const woTotal = woEdit ? recalc(woEdit.lines).reduce((s, l) => s + l.net, 0) : 0;
  const setLine = (i, patch) => setWoEdit({ ...woEdit, lines: recalc(woEdit.lines.map((l, j) => (j === i ? { ...l, ...patch } : l))) });
  const addLine = (line) => setWoEdit({ ...woEdit, lines: [...woEdit.lines, line] });
  const delLine = (i) => setWoEdit({ ...woEdit, lines: woEdit.lines.filter((_, j) => j !== i) });

  const woEditState = woEdit;
  const saveWo = async (ov) => {
    const woEdit = ov && ov.lines ? ov : woEditState;
    if (!woEdit) return;
    const payload = {
      client_id: cid, type: woEdit.type || "service", description: woEdit.description || "",
      status: woEdit.status || "booked", technician: woEdit.technician || "", scheduled_date: woEdit.scheduled_date || null,
      parts: [], labour: [], notes: woEdit.notes || "", lines: recalc(woEdit.lines),
      issue_text: woEdit.issue_text || "", result_text: woEdit.result_text || "", invoiced_by: woEdit.invoiced_by || brands[0] || "",
      work_type: woEdit.work_type || "Chargeable", warranty_authority: woEdit.warranty_authority || "",
      booked_by: woEdit.booked_by || "", date_booked: woEdit.date_booked || null, date_completed: woEdit.date_completed || null,
    };
    const savedWo = await api("PATCH", `/workorders/${woEdit.id}`, payload);
    toast.success(`Work Order ${woEdit.wo_number} saved`); loadAll();
    if (savedWo?.just_completed) setNextFor(savedWo);
  };

  // Popups (Add Part / Add Labour) commit straight onto the live Work Order, like the legacy forms.
  const addLineSave = async (line) => {
    const next = { ...woEditState, lines: recalc([...woEditState.lines, line]) };
    setWoEdit(next); await saveWo(next);
  };

  const addWorkOrder = async (opts = {}) => {
    const may = opts && opts.maytronics === true;
    const payload = {
      client_id: cid, type: "service", description: "", status: "wo_quotation", technician: "", scheduled_date: null,
      parts: [], labour: [], notes: "", lines: [],
      issue_text: may ? "Maytronics E-Sale #" : `New Work Order Quotation ${fmtDate(todayISO())}`,
      result_text: may ? "Maytronics E-Sale #" : "", invoiced_by: brands[0] || "", work_type: "Chargeable", warranty_authority: "",
      booked_by: user?.name || "", date_booked: todayISO(), date_completed: null,
    };
    setTab("work_orders");
    setWoSetup({ ...payload, _maytronics: may });
  };

  // "Done" on the New Work Order Setup popup -> write status / pink detail / employee onto the live WO.
  const saveWoSetup = async (fields) => {
    if (!woSetup) return;
    const w = woSetup;
    const payload = {
      client_id: cid, type: w.type || "service", description: w.description || "",
      status: fields.status, technician: fields.technician || "", scheduled_date: w.scheduled_date || null,
      parts: w.parts || [], labour: w.labour || [], notes: w.notes || "", lines: w.lines || [],
      issue_text: fields.issue_text || "", result_text: w.result_text || "", invoiced_by: w.invoiced_by || brands[0] || "",
      work_type: w.work_type || "Chargeable", warranty_authority: w.warranty_authority || "",
      booked_by: w.booked_by || user?.name || "", date_booked: w.date_booked || todayISO(), date_completed: w.date_completed || null,
    };
    const saved = w.id ? await api("PATCH", `/workorders/${w.id}`, payload) : await api("POST", "/workorders", payload);
    setWoSetup(null);
    if (saved?.just_completed) setNextFor(saved);
    const fresh = await loadAll();
    const updated = (fresh || []).find((x) => x.id === saved.id);
    setSelWoId(saved.id); setTab("work_orders");
    if (updated) setWoEdit(hydrateWo(updated));
    toast.success(`Work Order ${saved.wo_number} ${w.id ? "set up" : "created"}`);
  };

  // One-shot auto action requested by the Add New Client popup (New Work Order / Spa Lead / Maytronics).
  const autoRan = useRef(false);
  useEffect(() => {
    if (autoRan.current || !autoAction) return;
    autoRan.current = true;
    (async () => {
      try {
        if (autoAction === "work_order") await addWorkOrder();
        else if (autoAction === "maytronics") await addWorkOrder({ maytronics: true });
        else if (autoAction === "spa_lead") { setTab("quotes"); setQuoteEditor({ kind: "spa" }); }
      } finally { onAutoActionDone && onAutoActionDone(); }
    })();
  }, [autoAction]); // eslint-disable-line react-hooks/exhaustive-deps

  const addReminder = async () => {
    if (!rem.text.trim()) return;
    await api("POST", "/reminders", { text: rem.text.trim(), client_id: cid, due_date: rem.due_date, type: "general", assigned_to: rem.assignee || null, on_board: rem.onBoard ? true : null });
    setRem({ due_date: todayISO(), assignee: "", text: "" }); setShowRemModal(false); loadAll();
  };
  const addNote = async () => {
    if (!noteText.trim() && !noteSubj.trim()) return;
    await api("POST", `/clients/${cid}/notes`, { text: noteText.trim(), subject: noteSubj.trim() });
    setNoteText(""); setNoteSubj(""); setShowNoteModal(false); loadAll();
  };

  // Selecting a legacy (Spa Buddy) invoice promotes it to a full editable native invoice
  // so every invoice button works exactly like a new invoice.
  const selectInvoice = async (id) => {
    if (typeof id === "string" && id.startsWith("L")) {
      try {
        const sale = await api("POST", `/legacy/invoices/${id.slice(1)}/promote`);
        await loadAll();
        setSelSaleId(sale.id);
      } catch (e) { toast.error(e.message); setSelSaleId(id); }
    } else setSelSaleId(id);
  };
  const selectQuote = async (id) => {
    if (typeof id === "string" && id.startsWith("L")) {
      try {
        const q = await api("POST", `/legacy/quotes/${id.slice(1)}/promote`);
        await loadAll();
        setSelQuoteId(q.id);
      } catch (e) { toast.error(e.message); setSelQuoteId(id); }
    } else setSelQuoteId(id);
  };
  const selectWo = (id) => { setTab("work_orders"); setSelWoId(id); };
  // Form_Orders "Go To Client": open the order's client at its Work Order
  const goToOrder = (clientId, woId) => {
    if (clientId === cid) { reloadSyncWo?.(); if (woId) selectWo(woId); else setTab("work_orders"); return; }
    onNav?.(`client:${clientId}${woId ? `:wo:${woId}` : ""}`);
  };
  // Quote row click: Work Order quote -> open its Work Order (or the WO-quote editor if none linked yet); Spa/legacy quote -> show in the quote window.
  const openQuoteRow = (id) => {
    const q = quotesForTab.find((x) => x.id === id) || mergedQuotes.find((x) => x.id === id);
    if (q && q._woQuote) { selectWo(q.wo_id); return; }
    if (q && !q._legacy && q.kind === "work_order") {
      const linkedWo = wos.find((w) => w.quote_id === q.id) || (q.converted_wo_id ? wos.find((w) => w.id === q.converted_wo_id) : null);
      if (linkedWo) { selectWo(linkedWo.id); return; }
      setQuoteEditor({ kind: "work_order", quote: q });
      return;
    }
    selectQuote(id);
  };
  const emailDraft = (subject) => openEmail({ kind: "client", subject });

  // ---- Legacy (Spa Buddy) records folded into their own named tabs ----
  // Map a legacy invoice number -> the legacy work order that raised it (link restored from the migration source)
  const woByInvoiceNo = useMemo(() => {
    const m = {};
    (legacy.workorders || []).forEach((w) => { if (w.invoice_number) m[String(w.invoice_number)] = w.legacy_id; });
    return m;
  }, [legacy.workorders]);
  const legInvoices = useMemo(() => (legacy.invoices || []).map((r) => ({
    id: "L" + r.legacy_id, _legacy: true, sale_number: r.jnumber || ("J" + r.legacy_id), legacy_id: r.legacy_id,
    created_at: r.date, _date: r.date, status: r.status || "—", total: Number(r.nett || 0),
    gross: r.gross, discount: r.discount, nett: r.nett, balance_due: r.balance_due,
    salesperson: r.salesperson, invoice_to: r.invoice_to, delivery_date: r.delivery_date, items: [],
    wo_legacy_id: woByInvoiceNo[String(r.legacy_id)] || null,
  })), [legacy.invoices, woByInvoiceNo]);
  const legQuotes = useMemo(() => (legacy.quotes || []).map((r) => ({
    id: "L" + r.legacy_id, _legacy: true, quote_number: r.legacy_id, kind: "legacy",
    created_at: r.date, _date: r.date, status: r.status || (r.active || "—"), total: Number(r.amount || 0),
    active: r.active, gross: r.gross, discount: r.discount, amount: r.amount, salesperson: r.salesperson, quote_to: r.quote_to, items: [],
  })), [legacy.quotes]);
  const legWos = useMemo(() => (legacy.workorders || []).map((r) => ({
    id: "L" + r.legacy_id, _legacy: true, wo_number: r.legacy_id, status: r.status || "—",
    date_booked: r.booked, date_completed: r.completed, _date: r.booked, type: r.type,
    issue_text: r.issue, result_text: r.result, technician: r.carried_out_by, taken_by: r.taken_by,
    make: r.make, model: r.model, serial: r.serial,
    invoice_number: r.invoice_number || "", quote_number: r.quote_number || "",
  })), [legacy.workorders]);
  const byDateDesc = (a, b) => String(b._date || b.created_at || "").localeCompare(String(a._date || a.created_at || ""));
  const mergedSales = useMemo(() => [...sales, ...legInvoices].sort(byDateDesc), [sales, legInvoices]);
  const mergedQuotes = useMemo(() => [...quotes, ...legQuotes].sort(byDateDesc), [quotes, legQuotes]);
  const activeQuotes = useMemo(() => mergedQuotes.filter((q) => q._legacy
    ? String(q.active || "").toLowerCase() === "active"
    : !q.converted_wo_id && !q.invoice_id && !["converted", "lost", "declined", "invoiced", "cancelled", "no sale"].includes(String(q.status || "").toLowerCase())
  ), [mergedQuotes]);
  const mergedWos = useMemo(() => [...wos, ...legWos].sort(byDateDesc), [wos, legWos]);
  // A native Work Order whose status is "Work Order Quotation" (and not yet invoiced) is an
  // OUTSTANDING quote — surface it in the Quotes tab. Clicking it opens the Work Order itself.
  const woQuotations = useMemo(() => (wos || [])
    .filter((w) => /quotation/i.test(String(statusLabel(w.status) || w.status || "")) && !w.invoice_id)
    .map((w) => ({
      id: "WOQ" + w.id, _woQuote: true, wo_id: w.id, wo_number: w.wo_number, quote_number: w.wo_number,
      status: "Work Order Quotation", kind: "work_order",
      created_at: w.created_at, _date: w.date_booked || w.created_at, total: w.total || 0,
    })), [wos]);
  const quotesForTab = useMemo(() => [...woQuotations, ...activeQuotes].sort(byDateDesc), [woQuotations, activeQuotes]);

  const selQuote = mergedQuotes.find((q) => q.id === selQuoteId) || null;
  const selSale = mergedSales.find((s) => s.id === selSaleId) || null;
  const selLegacyWo = useMemo(() => legWos.find((w) => w.id === selWoId) || null, [legWos, selWoId]);
  // Open Spa Buddy work orders become normal editable work orders as soon as they're opened; completed ones stay read-only.
  useEffect(() => {
    if (!selLegacyWo || isLockedStatus(selLegacyWo.status) || selLegacyWo.invoice_number) return;
    api("POST", `/legacy/workorders/${selLegacyWo.wo_number}/promote`)
      .then(async (w) => { await loadAll(); setSelWoId(w.id); })
      .catch((e) => toast.error(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selLegacyWo]);

  const TABS = [
    ["client_details", "Client Details"], ["purchases", "Client Purchases"], ["work_orders", "Work Orders"], ["quotes", "Quotes"],
    ["invoices", "Invoices"], ["contracts", "Contracts"], ["equipment", "Equipment"],
    ["notes", "Notes"], ["water_tests", "Water Test"],
  ];

  return (
    <div style={{ ...gold, height: "calc(100vh - 4.5rem)" }} className="-mx-4 sm:-mx-6 lg:-mx-8 -mt-6 -mb-24 md:-mb-8 p-2 overflow-hidden flex flex-col" data-testid="client-overview">
      {/* HEADER */}
      <div className="flex flex-wrap gap-2 items-stretch shrink-0">
        <div className="w-44 flex flex-col bg-gradient-to-b from-[#e9d074] to-[#c8a12a] border border-[#8a6d16] rounded-sm py-2 px-2 cursor-pointer" onClick={() => setTab("contracts")} data-testid="co-contract-box" title="View service contracts">
          <span className="text-[13px] font-black text-slate-900 underline text-center block shrink-0">Client's Contract</span>
          <div className="flex-1 min-h-0 overflow-y-auto mt-1 flex flex-col items-center justify-center gap-1.5">
            {contracts.length > 0 ? (
              contracts.map((ct) => (
                <div key={ct.id} className="text-center w-full">
                  <div className="text-[10.5px] font-semibold text-slate-900 leading-snug" data-testid="co-contract-desc">{(ct.description || "").replace(/\n/g, " ")}</div>
                  <span className="inline-block mt-1 text-[10px] font-bold text-emerald-900 bg-emerald-200 border border-emerald-700 rounded px-1.5 py-0.5" data-testid="co-contract-badge">
                    {ct.frequency}{ct.fee ? ` \u00b7 $${ct.fee}` : ""}
                  </span>
                </div>
              ))
            ) : (
              <>
                {client.loyalty_number && <span className="text-2xl font-black text-[#1a4f8a] underline" data-testid="co-contract-no">{client.loyalty_number}</span>}
                <span className="text-[10px] text-slate-700/70">No service contract</span>
              </>
            )}
          </div>
        </div>
        <div className="flex-1 min-w-[340px] bg-gradient-to-b from-[#f3e6b8] to-[#e7d190] border border-[#c8a12a] rounded-sm px-4 py-1.5">
          <div className="text-center text-xl font-black text-[#123a63] leading-tight" data-testid="co-title">{title}</div>
          {street && <div className="text-center text-lg font-bold text-[#123a63]">{street}</div>}
          {(() => {
            const t = (tests || []).find((x) => x.test_date || x.created_at);
            if (!t) return null;
            const today = new Date().toLocaleDateString("en-CA");
            const last = t.test_date ? t.test_date.slice(0, 10) : t.created_at ? new Date(t.created_at).toLocaleDateString("en-CA") : "";
            const next = t.next_test_date || "";
            const overdue = next && next < today;
            const f = (d) => (d ? new Date(d).toLocaleDateString("en-AU") : "");
            return (
              <div className="text-center mt-0.5" data-testid="co-water-glance">
                <span className={`inline-block text-[11px] font-black px-2 py-0.5 rounded-full border ${overdue ? "bg-rose-100 text-rose-800 border-rose-400" : "bg-emerald-100 text-emerald-800 border-emerald-500"}`}>
                  Last test {f(last)}{next ? ` · Next due ${f(next)}` : ""}{overdue ? " · OVERDUE" : ""}
                </span>
              </div>
            );
          })()}
          {client.do_not_contact && (
            <div className="text-center mt-0.5" data-testid="co-notify">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600"><BellOff className="h-3 w-3" /> Do not contact</span>
            </div>
          )}
          <div className="mt-2 border-t border-[#c8a12a]/50 pt-1">
            <div className="space-y-0.5 max-h-[132px] overflow-y-auto pr-1" data-testid="co-contact-grid">
              {contactRows.map((c, i) => {
                const methods = ((c.details && c.details.length) ? c.details : [
                  ...(c.mobile ? [{ type: "Mobile", value: c.mobile }] : []),
                  ...(c.phone ? [{ type: "Phone", value: c.phone }] : []),
                ].filter((m) => m.value)).filter((m) => !(/email|@/i.test(m.type || "") || /@/.test(m.value || "")));
                return (
                <div key={i} data-testid="co-contact-row">
                  <div className="grid grid-cols-[1fr_1.2fr] gap-2 text-[13px] text-slate-800 items-start">
                    <span className="font-bold text-[14px] truncate flex items-center flex-wrap gap-1">{c.first_name} {c.surname}
                      {c.default_accounts && <span title="Accounts / invoices" className="text-[9px] font-bold text-white bg-emerald-600 rounded px-1">ACCOUNTS</span>}
                      {c.default_quotes && <span title="Quotes" className="text-[9px] font-bold text-white bg-sky-600 rounded px-1">QUOTES</span>}
                      {c.default_correspondence && <span title="Correspondence" className="text-[9px] font-bold text-white bg-violet-600 rounded px-1">MAIL</span>}
                      {c.relationship ? <span className="text-slate-500 font-normal">· {c.relationship}</span> : null}</span>
                    <span className="flex flex-wrap gap-x-2 gap-y-0.5 justify-end">
                      {methods.length === 0 ? <span className="text-slate-300 text-[11px]">—</span> : methods.map((m, mi) => {
                        const isEmail = /email|@/i.test(m.type || "") || /@/.test(m.value || "");
                        return <a key={mi} href={isEmail ? `mailto:${m.value}` : `tel:${(m.value || "").replace(/\s/g, "")}`} data-testid={isEmail ? "contact-mailto" : "contact-tel"} title={m.type} className="font-mono2 font-bold text-blue-700 hover:underline text-[14px] whitespace-nowrap">{m.value}</a>;
                      })}
                    </span>
                  </div>
                  {c.note && <div className="text-[11px] italic text-slate-500 pl-1 -mt-0.5">📝 {c.note}</div>}
                </div>
                );
              })}
            </div>
          </div>
        </div>
        <div className="flex items-stretch gap-2">
          {/* round action buttons */}
          <div className="flex flex-col gap-2 items-center justify-center">
            <button onClick={() => (onNav ? onNav("tab:reminders") : setShowRemList(true))} data-testid="co-reminder-btn" title="Reminders" className="relative h-[68px] w-[68px] rounded-full bg-gradient-to-br from-rose-500 to-red-700 border-[3px] border-white shadow-lg text-white flex flex-col items-center justify-center gap-0.5">
              <Bell className="h-5 w-5" /><span className="font-display font-extrabold text-[9px] leading-none">REMINDER</span>
              {remBadge > 0 && <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-white text-rose-700 text-[11px] font-extrabold border-2 border-rose-600 flex items-center justify-center" data-testid="co-reminder-count">{remBadge > 999 ? "999+" : remBadge}</span>}
            </button>
            <button onClick={() => (onNav ? onNav("tab:search") : onClose())} data-testid="co-search-btn" title="Search / all clients" className="h-[68px] w-[68px] rounded-full bg-gradient-to-b from-[#6aa8ff] to-[#1e5fc8] border-[3px] border-white shadow-lg text-white flex flex-col items-center justify-center gap-0.5"><Search className="h-5 w-5" /><span className="font-display font-extrabold text-[9px] leading-none">SEARCH</span></button>
            <button onClick={() => onGoTo && onGoTo("pos")} data-testid="co-goto-pos-btn" title="Counter POS — new walk-in sale (client not in system)" className="h-[68px] w-[68px] rounded-full bg-gradient-to-br from-emerald-500 to-emerald-700 border-[3px] border-white shadow-lg text-white flex flex-col items-center justify-center gap-0.5"><ShoppingCart className="h-5 w-5" /><span className="font-display font-extrabold text-[9px] leading-none text-center">COUNTER POS</span></button>
          </div>
          {/* vertical nav buttons */}
          <div className="flex flex-col gap-1.5 w-[210px] justify-center">
            <HdrBtn testid="co-email-client-btn" className="w-full !py-2 !text-[13px]" onClick={() => openEmail({ kind: "client" })}>Email Client</HdrBtn>
            <HdrBtn testid="co-sms-client-btn" className="w-full !py-2 !text-[13px]" onClick={() => setShowSms(true)}>SMS Client</HdrBtn>
            {showSms && <SmsClientModal client={client} onClose={() => setShowSms(false)} />}
            {mailNew > 0 && <button onClick={() => window.dispatchEvent(new CustomEvent("aqualux:open-inbox", { detail: { clientId: client.id } }))} data-testid="co-new-replies"
              className="w-full rounded-sm bg-red-600 hover:bg-red-700 text-white text-[12px] font-black py-1.5 animate-pulse">{mailNew} new {mailNew === 1 ? "reply" : "replies"}</button>}
            <HdrBtn testid="co-client-files-btn" className="w-full !py-2 !text-[13px]" onClick={() => setShowFiles(true)}>Client's Files</HdrBtn>
            <HdrBtn testid="co-client-orders-btn" className="w-full !py-2 !text-[13px]" onClick={() => setShowPartsOrders(true)}>View Client's Orders</HdrBtn>
            <HdrBtn testid="co-all-orders-btn" className="w-full !py-2 !text-[13px]" onClick={() => setShowAllOrders(true)}>View Parts Orders</HdrBtn>
            <HdrBtn testid="co-fm84-btn" className="w-full !py-2 !text-[13px]" onClick={() => setFm84Modal("list")}>View All Warranties</HdrBtn>
            <HdrBtn testid="co-switch-menu-btn" onClick={onSwitchboard} className="w-full !py-2 !text-[14px] !font-black !text-white !bg-none !bg-[#123a63] !border-[#0d2a47] hover:!brightness-110">DASHBOARD</HdrBtn>
          </div>
          {/* supplier image tiles (same as Dashboard) + Add New Client */}
          <div className="flex flex-col gap-1.5 w-[112px] justify-center">
            <button onClick={() => (onNav ? onNav("overlay:suppliers_folders") : setShowCompanyDocs(true))} data-testid="co-suppliers-folders-tile" className="rounded-2xl bg-gradient-to-b from-white to-[#dbe7f5] border border-[#9fc0e6] shadow-md p-1.5 flex flex-col items-center gap-0.5 hover:brightness-105 transition-[filter]">
              <img src="/icon-company-docs.png" alt="" className="h-9 w-9 object-contain drop-shadow" />
              <span className="font-display font-extrabold text-[11px] text-[#123a63] leading-tight text-center">Supplier's Folders</span>
            </button>
            <button onClick={() => (onNav ? onNav("overlay:business_contacts") : setShowSuppliers(true))} data-testid="co-supplier-contacts-tile" className="rounded-2xl bg-gradient-to-b from-white to-[#dbe7f5] border border-[#9fc0e6] shadow-md p-1.5 flex flex-col items-center gap-0.5 hover:brightness-105 transition-[filter]">
              <img src="/icon-supplier.webp" alt="" className="h-9 w-9 object-contain drop-shadow" />
              <span className="font-display font-extrabold text-[11px] text-[#123a63] leading-tight text-center">Supplier Contacts</span>
            </button>
            <button onClick={() => onNewClient && onNewClient()} data-testid="co-add-new-client-btn" title="Add a brand-new client" className="rounded-2xl bg-gradient-to-br from-green-500 to-emerald-700 border-2 border-white shadow-md px-2 py-2 flex items-center justify-center gap-1 text-white hover:brightness-110 transition-[filter]">
              <Plus className="h-4 w-4 shrink-0" /><span className="font-display font-black text-[11px] leading-tight text-center">ADD NEW CLIENT</span>
            </button>
          </div>
          <button onClick={onClose} data-testid="co-close-btn" className="self-stretch w-12 shrink-0 rounded-sm bg-black text-white flex flex-col items-center justify-center border border-white/40 hover:bg-slate-800">
            <X className="h-5 w-5" /><span className="text-[9px] font-bold mt-1">CLOSE</span>
          </button>
        </div>
      </div>

      {/* TAB BAR */}
      <div className="mt-2 flex items-end justify-between gap-2 shrink-0 border-b-2 border-[#8a6d16]">
        <div className="flex flex-wrap gap-0.5 min-w-0">
          {TABS.map(([k, label]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`co-tab-${k}`}
              className={`px-4 py-2 text-[13px] font-bold rounded-t-lg border border-[#8a6d16] -mb-[2px] transition-colors ${tab === k
                ? "bg-white text-[#123a63] border-b-white shadow-[0_-2px_5px_rgba(0,0,0,0.10)] relative z-10"
                : "bg-[#e3c968] text-[#4a3a0a] border-b-[#8a6d16] hover:bg-[#edd889]"}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 pb-1 shrink-0">
          <LoyaltyField client={client} />
          <button onClick={() => onSellAtPos && onSellAtPos(client, [], brand)} data-testid="co-loyalty-goto-pos-btn" title={`Open POS with ${client.name} attached`} className="inline-flex items-center gap-1 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white font-black text-[12px] px-2.5 py-1 shadow whitespace-nowrap">
            <ShoppingCart className="h-4 w-4" /> CLIENT'S POS
          </button>
        </div>
      </div>

      {/* BODY */}
      {showRemList && (
        <div className="fixed inset-0 z-[85] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowRemList(false)} data-testid="co-reminder-list-popup">
          <div className="bg-white rounded shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-3 py-2 bg-rose-700 text-white text-sm font-black"><span>Reminders · {client.display_name || client.name}</span><button onClick={() => setShowRemList(false)} data-testid="co-reminder-list-close"><X className="h-5 w-5" /></button></div>
            <div className="p-2 flex-1 min-h-0 overflow-y-auto divide-y divide-slate-200">
              {reminders.filter((r) => !r.done).length === 0 && <p className="py-4 text-sm text-slate-400 text-center">No open reminders.</p>}
              {reminders.filter((r) => !r.done).map((r) => <ClientReminderCard key={r.id} r={r} team={team} onChanged={loadAll} />)}
            </div>
            <div className="p-2 border-t"><button onClick={() => { setShowRemList(false); setRem({ due_date: todayISO(), assignee: "", text: "" }); setShowRemModal(true); }} data-testid="co-reminder-list-add" className="w-full text-sm font-bold bg-[#123a63] text-white py-2 rounded-sm">ADD REMINDER</button></div>
          </div>
        </div>
      )}
      <div className="mt-0 flex gap-2 items-stretch flex-1 min-h-0">
        <div className="w-56 xl:w-64 2xl:w-[17rem] shrink-0 flex flex-col gap-2 min-h-0" data-testid="co-left-lists">
          <div className="bg-white/80 border border-[#8a6d16] rounded-sm flex-[1_1_0%] min-h-[9rem] flex flex-col">
            <div className="bg-[#d6dfec] text-center text-[12px] font-black text-[#123a63] py-1 border-b border-[#5b8ac4]">REMINDERS</div>
            <div className="p-1.5 space-y-1 flex-1 min-h-0 flex flex-col">
              <div className="flex-1 min-h-0 overflow-y-auto divide-y divide-slate-300">
                {reminders.filter((r) => !r.done).length === 0 && <p className="py-1.5 text-[11px] text-slate-400 text-center">No reminders.</p>}
                {reminders.filter((r) => !r.done).map((r) => <ClientReminderCard key={r.id} r={r} team={team} onChanged={loadAll} />)}
              </div>
              <button onClick={() => { setRem({ due_date: todayISO(), assignee: "", text: "" }); setShowRemModal(true); }} data-testid="co-reminder-add" className="w-full text-[11px] font-bold bg-[#123a63] text-white py-1 rounded-sm hover:bg-[#1a4f8a]">ADD REMINDER</button>
            </div>
          </div>
          <div className="bg-white/80 border border-[#8a6d16] rounded-sm shrink min-h-[4.5rem] flex flex-col">
            <div className="bg-[#d6dfec] text-center text-[12px] font-black text-[#123a63] py-1 border-b border-[#5b8ac4]">WORK ORDERS</div>
            <div className="h-[125px] min-h-0 shrink overflow-y-auto" data-testid="co-wo-list">
              {mergedWos.length === 0 && <p className="p-2 text-[11px] text-slate-500">None yet.</p>}
              {mergedWos.map((w) => {
                const st = (w._legacy ? w.status : statusLabel(w.status)) || "";
                const stShort = String(st).replace(/^work order\s+/i, "");
                const done = /complet|invoic|closed|done|counter sale|paid/i.test(String(st));
                const dateShown = done ? fmtDate(w.date_completed || w.completed || w.date_booked || w._date) : "";
                return (
                <button key={w.id} onClick={() => selectWo(w.id)} data-testid="co-wo-quick"
                  className={`w-full h-[25px] grid grid-cols-[auto_1fr_auto] gap-1 px-2 text-[11px] text-left border-b border-slate-200 items-center ${selWoId === w.id ? "bg-[#cfe0f5]" : "hover:bg-slate-50"}`}>
                  <span className="font-mono2 font-bold text-[#123a63] flex items-center gap-1">{refNo("workorder", w.wo_number)}{w._legacy && <span className="text-[8px] font-black text-amber-700 bg-amber-100 border border-amber-300 rounded px-0.5">LEG</span>}</span>
                  <span className="truncate text-slate-700" title={st}>{stShort}</span>
                  <span className="font-mono2 text-slate-500 text-right whitespace-nowrap">{dateShown}</span>
                </button>
                );
              })}
            </div>
            <div className="p-1.5 border-t border-slate-200">
              <button onClick={addWorkOrder} data-testid="co-add-work-order-btn" className="w-full text-[11px] font-bold bg-[#123a63] text-white py-1 rounded-sm hover:bg-[#1a4f8a]">ADD WORK ORDER</button>
            </div>
          </div>
          <div className="bg-white/80 border border-[#8a6d16] rounded-sm shrink min-h-[4.5rem] flex flex-col">
            <div className="bg-[#d6dfec] text-center text-[12px] font-black text-[#123a63] py-1 border-b border-[#5b8ac4]">NOTES</div>
            <div className="h-[125px] min-h-0 shrink overflow-y-auto" data-testid="co-notes-list">
              {notes.length === 0 && <p className="p-2 text-[11px] text-slate-500">No notes yet.</p>}
              {notes.slice(0, 50).map((n) => (
                <button key={n.id} onClick={() => setViewNote(n)} data-testid="co-note-row"
                  className="w-full h-[25px] text-left px-2 text-[11px] border-b border-slate-200 text-slate-700 hover:bg-[#eef3f9] cursor-pointer flex gap-2 items-center">
                  <b className="font-mono2 shrink-0">{fmtDate(n.created_at)}</b>
                  <span className="truncate">{n.type || noteSubject(n.text)}</span>
                </button>
              ))}
            </div>
            <div className="p-1.5 border-t border-slate-200">
              <button onClick={() => { setNoteText(""); setNoteSubj(""); setShowNoteModal(true); }} data-testid="co-add-note-btn" className="w-full text-[11px] font-bold bg-[#123a63] text-white py-1 rounded-sm hover:bg-[#1a4f8a]">ADD NOTE</button>
            </div>
          </div>
        </div>

        <div className="flex-1 min-w-0 bg-white/90 border border-[#8a6d16] rounded-sm p-3 overflow-y-auto min-h-0">
          {tab === "client_details" && <ClientDetails client={client} onEdit={() => onEditClient(client)} cid={cid} bodies={bodies} reload={loadAll} onSaved={(updated) => onClientSaved && onClientSaved(updated)} />}
          {tab === "work_orders" && (selLegacyWo
            ? <LegacyWorkOrderView wo={selLegacyWo} client={client} brand={brand} onNewNative={addWorkOrder}
                onGoToInvoice={(invNo) => { const inv = mergedSales.find((s) => String(s.legacy_id || "") === String(invNo) || String(s.sale_number) === String(invNo) || String(s.sale_number) === ("J" + invNo)); if (inv) { setSelSaleId(inv.id); setTab("invoices"); } else { toast("Linked invoice not found on this client."); } }} />
            : (
            <WorkOrdersTab
              woEdit={woEdit} setWoEdit={setWoEdit} setLine={setLine} addLine={addLine} delLine={delLine}
              woTotal={woTotal} recalc={recalc} brands={brands} team={team} saveWo={saveWo}
              addWorkOrder={addWorkOrder} onPickPart={() => setPickPart(true)} onAddLabour={() => (woEdit?.invoice_id ? window.alert("You cannot add labour as this job has already been invoiced. You will need to edit from 'Invoice'.") : setPickLabour(true))}
              onWarranty={() => setFm84Modal({ wo: woEdit })} client={client}
              partsOrders={partsOrders} onOrderPart={() => setPartsOrderWo({ wo: woEdit })}
              onEmailWO={(w, attach, quote) => openEmail({ kind: quote ? "wo_quote" : "work_order", doc: w, attach })}
              isAdmin={isAdmin}
              onDeleteWo={async (w) => {
                if (!window.confirm(`Delete Work Order ${w.wo_number}? This cannot be undone.`)) return;
                try { await api("DELETE", `/workorders/${w.id}`); toast.success(`Work Order ${w.wo_number} deleted`); setWoEdit(null); setSelWoId(null); await loadAll(); }
                catch (e) { toast.error(e.message); }
              }}
              onGoToInvoice={(w) => { setSelSaleId(w.invoice_id); setTab("invoices"); }}
              onCreateInvoice={async (w) => {
                try {
                  if (woEditState && woEditState.id === w.id) await saveWo(woEditState); // invoice exactly what's on screen
                  const r = await api("POST", `/workorders/${w.id}/create-invoice`);
                  toast.success(`Invoice ${r.invoice.sale_number} created`);
                  await loadAll();
                  setWoEdit((prev) => (prev && prev.id === w.id ? { ...prev, invoice_id: r.invoice.id, status: "completed" } : prev));
                  setSelSaleId(r.invoice.id); setTab("invoices");
                } catch (e) { toast.error(e.message); }
              }}
            />
          ))}
          {tab === "quotes" && (
            <QuotesTab quotes={quotesForTab} sel={selQuote} onSelect={openQuoteRow}
              onNewQuote={(kind) => kind === "work_order" ? addWorkOrder() : setQuoteEditor({ kind })}
              onEdit={(q) => setQuoteEditor({ kind: q.kind || "work_order", quote: q })}
              onPrint={(q) => printQuoteDoc(q, client, q.brand || brand)}
              onEmail={(q) => openEmail({ kind: q.kind === "work_order" ? "wo_quote" : "spa_quote", doc: q, attach: () => printQuoteDoc(q, client, q.brand || brand) })}
              onCreateInvoice={async (q) => { try { const r = await api("POST", `/quotes/${q.id}/invoice`); toast.success(`Invoice ${r.invoice.sale_number} created`); await loadAll(); setSelSaleId(r.invoice.id); setTab("invoices"); } catch (e) { toast.error(e.message); } }}
              onDelete={async (q) => { if (window.confirm(`Delete quote ${q.quote_number}?`)) { await api("DELETE", `/quotes/${q.id}`); setSelQuoteId(null); loadAll(); } }}
              onNoSale={async (q) => { try { await api("POST", `/quotes/${q.id}/no-sale`); toast.success(`Quote ${q.quote_number} marked No Sale`); setSelQuoteId(null); await loadAll(); } catch (e) { toast.error(e.message); } }}
              onReactivate={async (q) => { try { await api("POST", `/quotes/${q.id}/reactivate`); toast.success(`Quote ${q.quote_number} reactivated`); await loadAll(); } catch (e) { toast.error(e.message); } }}
              onQuoteToDetails={() => onEditClient(client)}
              isAdmin={isAdmin} client={client} brand={brand} />
          )}
          {tab === "invoices" && <InvoicesTab sales={mergedSales} sel={selSale} onSelect={selectInvoice}
            onNewInvoice={() => setInvoiceBuilder(true)} onPrint={(s) => printInvoiceDoc(s, client, brand, wos)}
            onViewInvoice={(s) => printInvoiceDoc(s, client, brand, wos)}
            onEmailInvoice={(s) => openEmail({ kind: "invoice", doc: s, attach: () => printInvoiceDoc(s, client, brand, wos) })}
            onViewStatement={() => printLegacyStatement(client, mergedSales, brand)}
            onPrintStatement={() => printLegacyStatement(client, mergedSales, brand)}
            onEmailStatement={(s) => { if (s && Math.max((s.total || 0) - (s.payments || []).reduce((a, p) => a + (p.amount || 0), 0), 0) === 0 && !s._legacy) { toast.error("There is no amount outstanding on this invoice in order to warrent the creation of a Statement"); return; } openEmail({ kind: "statement", doc: s, attach: () => printLegacyStatement(client, mergedSales, brand) }); }}
            onViewDelivery={(s) => printLegacyDelivery(s, client, brand)}
            onEditInvoice={(s) => setInvoiceEditor(s)}
            onGoToWorkOrder={(s) => {
              if (s.wo_id) { setSelWoId(s.wo_id); setTab("work_orders"); return; }
              const woLeg = s.wo_legacy_id || woByInvoiceNo[String(s.legacy_id || "")];
              if (woLeg) { setSelWoId("L" + woLeg); setTab("work_orders"); return; }
              toast("No work order is linked to this invoice.");
            }}
            onPay={(s) => setPayModal(s)} onEditDetails={(s) => setInvDetails(s)}
            onStatement={() => printLegacyStatement(client, mergedSales, brand)}
            onDelivery={(s) => printLegacyDelivery(s, client, brand)}
            onEmailReceipt={(s) => openEmail({ kind: "receipt", doc: s, attach: () => printInvoiceDoc(s, client, brand, wos) })}
            onMarkDelivered={async (s) => { try { await api("POST", `/sales/${s.id}/mark-delivered`); toast.success("Marked SOLD DELIVERED · reminder set"); await loadAll(); } catch (e) { toast.error(e.message); } }}
            onSentToday={async (s) => { try { await api("POST", `/sales/${s.id}/mark-sent-today`); toast.success("Invoice Sent date stamped today"); await loadAll(); } catch (e) { toast.error(e.message); } }}
            onDeliveryBy={(s) => setDeliveryBy(s)}
            onPaymentArrangement={(s) => setPayArrange(s)}
            onStockAllocator={(s) => { if ((s.status || "") === "Work Order Completed") { toast.error("This Invoice is a Work Order hence a J Number cannot be allocated."); } else { setStockAlloc(s); } }}
            isAdmin={isAdmin} client={client} brand={brand} />}
          {tab === "contracts" && <ContractsTab cid={cid} contracts={contracts} client={client} brand={brand} reload={loadAll} />}
          {tab === "equipment" && <EquipmentTab cid={cid} equipment={equipment} reload={loadAll} />}
          {tab === "notes" && <NotesTab notes={notes} reminders={reminders} onView={setViewNote} />}
          {tab === "purchases" && <ClientPurchasesTab data={purch}
            onOpen={(p) => {
              if (p.wo_id) { setSelWoId(p.wo_id); setTab("work_orders"); return; }
              if (p.wo_legacy_id) { setSelWoId("L" + p.wo_legacy_id); setTab("work_orders"); return; }
              if (p.sale_id) { setSelSaleId(p.sale_id); setTab("invoices"); return; }
              toast("No work order is linked to this purchase.");
            }} />}
          {tab === "water_tests" && <WaterTestTab cid={cid} bodies={bodies} tests={tests} brands={brands} client={client} reload={loadAll} onSellAtPos={onSellAtPos} company={brand} onGoToClientDetails={() => setTab("client_details")} />}
        </div>
      </div>

      {/* MODALS */}
      {pickPart && woEdit && (
        <AddWorkOrderPart onClose={() => setPickPart(false)}
          onAdd={(p, qty) => { addLineSave({ kind: "part", item: p.name, product_id: p.id, unlocked: false, unit: p.price, qty, gross: p.price * qty, discount: 0, net: p.price * qty }); setPickPart(false); }} />
      )}
      {pickLabour && woEdit && (
        <AddLabour team={team} woTechnician={woEdit.technician || ""} onClose={() => setPickLabour(false)}
          onAdd={(line) => { addLineSave(line); setPickLabour(false); }} />
      )}
      {showFiles && <ClientFolders clientId={cid} clientName={client.name} onClose={() => setShowFiles(false)} />}
      {showCompanyDocs && <CompanyDocsModal docs={companyDocs} reload={loadCompanyDocs} onClose={() => setShowCompanyDocs(false)} />}
      {showSuppliers && <SuppliersModal suppliers={suppliers} reload={loadSuppliers} onClose={() => setShowSuppliers(false)} isAdmin={isAdmin} />}
      {showPartsOrders && (
        <PartsOrdersModal orders={partsOrders} reload={reloadSyncWo} onClose={() => setShowPartsOrders(false)}
          onNew={() => { setShowPartsOrders(false); setPartsOrderWo({}); }} />
      )}
      {partsOrderWo !== undefined && (
        <OrdersForm client={client} wo={partsOrderWo.wo || null} suppliers={suppliers} reload={reloadSyncWo} brand={brand} employee={user?.name || ""}
          onGoTo={goToOrder} onClose={() => setPartsOrderWo(undefined)} />
      )}
      {fm84Modal && (
        <FM84Modal cid={cid} client={client} fm84s={fm84s} team={team} suppliers={suppliers} wo={fm84Modal.wo || null}
          brand={brand} reload={loadAll} onClose={() => setFm84Modal(null)} />
      )}
      {payModal && <InvoicePaymentHow sale={payModal} client={client} reload={loadAll} onClose={() => setPayModal(null)} />}
      {showRemModal && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowRemModal(false)} data-testid="co-reminder-modal">
          <div className="bg-white rounded-sm border border-[#8a6d16] w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#123a63] text-white px-3 py-2 flex justify-between items-center">
              <span className="text-[13px] font-black">New Reminder</span>
              <button onClick={() => setShowRemModal(false)} data-testid="co-reminder-modal-close" className="hover:text-rose-300"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-3 space-y-2">
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="block text-[10px] font-black uppercase tracking-wide text-[#123a63] mb-0.5">Due date</label>
                  <input type="date" value={rem.due_date} onChange={(e) => setRem({ ...rem, due_date: e.target.value })} className={cellInp} data-testid="co-reminder-date" />
                </div>
                <div className="flex-1">
                  <label className="block text-[10px] font-black uppercase tracking-wide text-[#123a63] mb-0.5">Assign to</label>
                  <select value={rem.assignee} onChange={(e) => setRem({ ...rem, assignee: e.target.value })} className={cellInp} data-testid="co-reminder-assignee">
                    <option value="">Staff…</option>{team.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wide text-[#123a63] mb-0.5">Reminder</label>
                <textarea rows={3} autoFocus value={rem.text} onChange={(e) => setRem({ ...rem, text: e.target.value })} placeholder="What needs doing?" className={cellInp} data-testid="co-reminder-text" />
              </div>
              <label className="flex items-center gap-2 text-[12px] font-bold text-[#123a63] cursor-pointer">
                <input type="checkbox" checked={!!rem.onBoard} onChange={(e) => setRem({ ...rem, onBoard: e.target.checked })} data-testid="co-reminder-on-board" /> Place On White Board (create a magnet)
              </label>
              <div className="flex gap-2 pt-1">
                <button onClick={addReminder} data-testid="co-reminder-save" className="flex-1 text-[12px] font-black bg-[#123a63] text-white py-1.5 rounded-sm hover:bg-[#1a4f8a]">SAVE REMINDER</button>
                <button onClick={() => setShowRemModal(false)} className="px-3 text-[12px] font-bold border border-slate-300 rounded-sm hover:bg-slate-100">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showNoteModal && (
        <div className="fixed inset-0 z-[90] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowNoteModal(false)} data-testid="co-note-modal">
          <div className="bg-white rounded-sm border border-[#8a6d16] w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#123a63] text-white px-3 py-2 flex justify-between items-center">
              <span className="text-[13px] font-black">New Note</span>
              <button onClick={() => setShowNoteModal(false)} data-testid="co-note-modal-close" className="hover:text-rose-300"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-3 space-y-2">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wide text-[#123a63] mb-0.5">Subject (shows in the notes list)</label>
                <input type="text" autoFocus value={noteSubj} onChange={(e) => setNoteSubj(e.target.value)} placeholder="e.g. Parts Arrived, W/O Created, Payment F/U" className={cellInp} data-testid="co-note-subject" />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase tracking-wide text-[#123a63] mb-0.5">Details</label>
                <textarea rows={5} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Type the note…" className={cellInp} data-testid="co-note-input" />
              </div>
              <div className="flex gap-2 pt-1">
                <button onClick={addNote} data-testid="co-note-save" className="flex-1 text-[12px] font-black bg-[#123a63] text-white py-1.5 rounded-sm hover:bg-[#1a4f8a]">SAVE NOTE</button>
                <button onClick={() => setShowNoteModal(false)} className="px-3 text-[12px] font-bold border border-slate-300 rounded-sm hover:bg-slate-100">Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {viewNote && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setViewNote(null)} data-testid="co-note-viewer">
          <div className="bg-white rounded-sm border border-[#8a6d16] max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#123a63] text-white px-4 py-2 flex justify-between items-center gap-3">
              <span className="text-[13px] font-black">{viewNote.type ? `${viewNote.type} — ` : "Note — "}{fmtDate(viewNote.created_at)}{viewNote.employee ? ` · ${viewNote.employee}` : ""}</span>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => printNotes(client, [viewNote])} data-testid="co-note-print-btn" className="inline-flex items-center gap-1 rounded bg-white/15 hover:bg-white/25 text-white text-[11px] font-bold px-2 py-1">Print</button>
                <button onClick={() => { const subj = `${viewNote.type || "Note"} — ${fmtDate(viewNote.created_at)}`; setViewNote(null); openEmail({ kind: "client", subject: subj, body: viewNote.text }); }} data-testid="co-note-email-btn" className="inline-flex items-center gap-1 rounded bg-white/15 hover:bg-white/25 text-white text-[11px] font-bold px-2 py-1">Email</button>
                <button onClick={() => setViewNote(null)} data-testid="co-note-viewer-close" className="hover:text-rose-300"><X className="h-5 w-5" /></button>
              </div>
            </div>
            <div className="p-4 overflow-y-auto text-[13px] text-slate-800 whitespace-pre-wrap leading-relaxed">{viewNote.text}</div>
          </div>
        </div>
      )}
      {invDetails && <InvoiceDetailsModal sale={invDetails} client={client} reload={loadAll} onClose={() => setInvDetails(null)} />}
      {payArrange && <PaymentArrangementModal sale={payArrange} reload={loadAll} onClose={() => setPayArrange(null)} />}
      {deliveryBy && <DeliveryByModal sale={deliveryBy} suppliers={suppliers} reload={loadAll} onClose={() => setDeliveryBy(null)} />}
      {stockAlloc && <StockAllocatorModal sale={stockAlloc} reload={loadAll} onClose={() => setStockAlloc(null)} />}
      {quoteEditor && (
        <DocEditor title={quoteEditor.quote ? `Edit Quote ${quoteEditor.quote.quote_number}` : (quoteEditor.kind === "spa" ? "New Spa / Swim Spa / Urban Pool Quote" : "New Work Order Quote")}
          doc={quoteEditor.quote} accent="#4a5a1e" onClose={() => setQuoteEditor(null)} contacts={client.contacts}
          brands={brands} defaultBrand={quoteEditor.kind === "spa" ? "Just Spas Newcastle" : "Glamour Pools and Spas"}
          onSave={async ({ items, discount, notes, status, brand: qbrand, addressed_to, addressed_relationship, addressed_email }) => {
            const body = { client_id: cid, items, discount, notes, status, kind: quoteEditor.kind, brand: qbrand, addressed_to, addressed_relationship, addressed_email };
            if (!items || !items.length) { toast.error("Add at least one item — blank quotes are not saved"); return; }
            if (quoteEditor.quote) { await api("PATCH", `/quotes/${quoteEditor.quote.id}`, body); toast.success("Quote saved"); }
            else { const qd = await api("POST", "/quotes", body); toast.success(`Quote ${qd.quote_number} created`); setSelQuoteId(qd.id); }
            setQuoteEditor(null); loadAll();
          }} />
      )}
      {nextFor && <NextServiceModal wo={nextFor} onClose={(booked) => { setNextFor(null); if (booked) loadAll(); }} />}
      {woSetup && (
        <WorkOrderSetupModal wo={woSetup} client={client}
          contract={(contracts || []).find((c) => c.active) || (contracts || [])[0] || null}
          team={team} onClose={() => setWoSetup(null)} onDone={saveWoSetup} />
      )}
      {invoiceBuilder && (
        <DocEditor title="New Invoice" doc={null} accent="#123a63" hideStatus onClose={() => setInvoiceBuilder(false)} contacts={client.contacts}
          onSave={async ({ items, discount, notes, addressed_to, addressed_relationship, addressed_email }) => {
            const body = { client_id: cid, client_name: client.name,
              items: items.map((i) => ({ product_id: i.product_id, name: i.name, sku: "", qty: Math.round(i.qty) || 1, unit_price: i.unit_price, discount: i.discount || 0, colour_name: i.colour_name || "", colour_image: i.colour_image || "" })),
              discount: discount || 0, note: notes || "", payments: [], amount_tendered: 0,
              addressed_to, addressed_relationship, addressed_email };
            const sale = await api("POST", "/sales", body); toast.success(`Invoice ${sale.sale_number} created`);
            setInvoiceBuilder(false); await loadAll(); setSelSaleId(sale.id);
          }} />
      )}
      {invoiceEditor && (
        <DocEditor title={`Edit Invoice ${invoiceEditor.sale_number}`} doc={invoiceEditor} accent="#123a63" hideStatus onClose={() => setInvoiceEditor(null)} contacts={client.contacts}
          onSave={async ({ items, discount }) => {
            await api("PATCH", `/sales/${invoiceEditor.id}/items`, {
              items: items.map((i) => ({ product_id: i.product_id, name: i.name, sku: "", qty: i.qty, unit_price: i.unit_price, discount: i.discount || 0, colour_name: i.colour_name || "", colour_image: i.colour_image || "" })),
              discount: discount || 0,
            });
            toast.success(`Invoice ${invoiceEditor.sale_number} updated`);
            setInvoiceEditor(null); await loadAll();
          }} />
      )}
      {emailModal && <EmailRecipientsModal client={client} kind={emailModal.kind} subject={emailModal.subject} doc={emailModal.doc} body={emailModal.body} attach={emailModal.attach} brand={brand} employee={user?.name || ""} onSent={loadAll} onClose={() => setEmailModal(null)} />}
      {showAllOrders && <OrdersForm client={client} wo={null} suppliers={suppliers} reload={reloadSyncWo} brand={brand} employee={user?.name || ""}
        onGoTo={goToOrder} onClose={() => setShowAllOrders(false)} />}
    </div>
  );
}


// one card per purchase, same format as Pool-Tech; click the number to open the locked work order
function ClientPurchasesTab({ data, onOpen }) {
  const [q, setQ] = useState("");
  if (!data) return <p className="p-4 text-slate-400 text-sm" data-testid="co-purchases-loading">Loading purchases…</p>;
  const all = data.purchases || [];
  const hit = (p) => `${p.ref} ${p.salesperson || ""} ${p.items.map((i) => `${i.product} ${i.part_number} ${i.serial}`).join(" ")}`.toLowerCase().includes(q.toLowerCase());
  const list = q ? all.filter(hit) : all;
  return (
    <div data-testid="co-purchases-tab">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-black text-[#123a63]">Total spent with us:</span>
          <span data-testid="co-purchases-total" className="bg-white border border-[#b9b9b9] px-3 py-1 text-[14px] font-mono2 font-black text-emerald-700">{money(data.total_spent || 0)}</span>
          <span className="text-[11px] text-slate-500">({all.length} purchases)</span>
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="co-purchases-search" placeholder="Search product, part #, serial, invoice…"
          className="w-64 rounded border border-[#b9b9b9] px-3 py-1.5 text-[12px] outline-none focus:border-[#4f8fd6]" />
      </div>
      <div className="max-h-[calc(100vh-260px)] overflow-y-auto space-y-2 pr-1">
        {!list.length && <p className="p-3 text-slate-400 text-sm" data-testid="co-purchases-empty">No purchases on record{q ? " match your search" : ""}.</p>}
        {list.map((p) => (
          <div key={`${p.source}-${p.ref}`} data-testid="co-purchase-card" className="bg-white border border-[#b9b9b9] rounded px-3 py-2">
            <div className="flex justify-between gap-2 text-[14px] font-black text-[#123a63]">
              <button onClick={() => onOpen(p)} data-testid={`co-purchase-open-${p.ref}`} title="Open the work order" className="underline underline-offset-4 decoration-2 decoration-sky-400 hover:text-sky-700">#{p.ref}</button>
              <span className="font-mono2">{money(p.total)}</span>
            </div>
            <div className="text-[11px] text-slate-500 mb-1">{fmtDate(p.date)}{p.salesperson ? ` · ${p.salesperson}` : ""}{p.status ? ` · ${p.status}` : ""}</div>
            {p.items.map((i, n) => (
              <div key={n} className="flex justify-between gap-3 text-[12px] text-slate-800">
                <span>{i.qty}× {[i.product, i.make, i.model].filter(Boolean).join(" ")}{i.colour ? ` — ${i.colour}` : ""}{i.serial ? <span className="text-slate-500"> · S/N {i.serial}</span> : null}</span>
                <span className="font-mono2 text-slate-600 whitespace-nowrap">{money(i.net)}</span>
              </div>
            ))}
          </div>
        ))}
      </div>
      {q && <p className="text-[11px] text-slate-500 mt-1">Filtered total: <b>{money(list.reduce((a, p) => a + (p.total || 0), 0))}</b> across {list.length} purchases</p>}
    </div>
  );
}
